import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { useBrandAppearance } from "../hooks/useBrandAppearance";

interface UnsavedChangesDialogProps {
    open: boolean;
    onStay: () => void;
    onLeave: () => void;
}

/**
 * Confirmation dialog shown when the user tries to navigate away from the
 * guest details form while they have unsaved local changes.
 */
const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
    open,
    onStay,
    onLeave,
}) => {
    const { t } = useTranslation("guest_details");
    const { colors: BrandColors } = useBrandAppearance();

    return (
        <Dialog
            open={open}
            onClose={onStay}
            aria-labelledby="unsaved-changes-title"
            aria-describedby="unsaved-changes-description"
            PaperProps={{
                sx: {
                    borderRadius: 1,
                    backgroundColor: "var(--oc-paper-bg)",
                    color: "var(--oc-text)",
                    maxWidth: 460,
                },
            }}
        >
            <DialogTitle
                id="unsaved-changes-title"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "var(--oc-text)",
                    fontWeight: 600,
                }}
            >
                <WarningAmberIcon sx={{ color: BrandColors.accent }} />
                {t("unsavedChanges.title", { defaultValue: "Unsaved changes" })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id="unsaved-changes-description"
                    sx={{ color: "var(--oc-text)" }}
                >
                    {t("unsavedChanges.message", {
                        defaultValue:
                            "You have unsaved changes on this guest. If you leave now, your changes won't be saved. Are you sure you want to continue?",
                    })}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: "100%", justifyContent: "flex-end" }}>
                    <Button
                        onClick={onStay}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderColor: BrandColors.primary,
                            color: BrandColors.primary,
                            "&:hover": {
                                borderColor: BrandColors.primary,
                                backgroundColor: "rgba(128, 128, 128, 0.08)",
                            },
                        }}
                    >
                        {t("unsavedChanges.stay", { defaultValue: "Stay on this page" })}
                    </Button>
                    <Button
                        onClick={onLeave}
                        variant="contained"
                        sx={{
                            borderRadius: 0,
                            backgroundColor: BrandColors.accent,
                            color: "#FFFFFF",
                            "&:hover": { backgroundColor: BrandColors.accent },
                        }}
                    >
                        {t("unsavedChanges.leave", { defaultValue: "Leave anyway" })}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UnsavedChangesDialog;
